import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import RouterContext from 'helpers/routerContext';
import { adopt } from 'react-adopt';
import { Query } from 'react-apollo';
import gql from 'graphql-tag';
import { PageInfo } from 'components/common/Paginator';
import { defaultNumberOfRows as DEFAULT_NUMBER_OF_ROWS } from 'config';
import qs, { getQueryString } from 'helpers/queryStrings';
import CreditList from './List';

/**
 * Query to fetch expired ads from api
 */
export const CREDIT_TRANSACTION_LIST = gql`
  query CreditTransactions(
    $first: Int
    $last: Int
    $before: String
    $after: String
    $creditType: [FilterByType]
    $createdOnGte: DateTime
    $createdOnLte: DateTime
    $user: ID
  ) {
    creditTransactions(
      first: $first
      after: $after
      last: $last
      before: $before
      creditTransactionType: $creditType
      createdOnGte: $createdOnGte
      createdOnLte: $createdOnLte
      userId: $user
    ) {
      edges {
        node {
          id
          type
          createdOn
          amount
          user {
            id
            email
            fullname
          }
        }
      }
      pageInfo {
        ...PaginatorPageInfo
      }
    }
  }
  ${PageInfo}
`;

/**
 * Default filters
 */
export const defaultFilters = {
  startDateGte: undefined,
  startDateLte: undefined,
  user: undefined,
  creditType: undefined,
};

class CreditsListContainer extends Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    const {
      numberOfRowsSelected,
      direction,
      cursor,
      ...restFilters
    } = props.initialFilters;

    this.state = {
      numberOfRowsSelected:
        parseInt(numberOfRowsSelected, 0) || DEFAULT_NUMBER_OF_ROWS,
      pagination: { direction: direction || null, cursor: cursor || undefined },
      filters: {
        ...defaultFilters,
        ...restFilters,
      },
    };

    this.Compose = adopt({
      listCreditTransactions: this.listCreditTransactions,
    });
  }

  /**
   * @description
   * Query callback for compose.
   *
   * @param {*} render - Query's render props.
   * @returns {*} Query component.
   */
  listCreditTransactions = ({ render }) => {
    const { filters, numberOfRowsSelected, pagination } = this.state;
    const paginationVariables =
      !pagination.direction || pagination.direction === 'NEXT'
        ? {
            first: numberOfRowsSelected,
            after: pagination.cursor,
          }
        : {
            last: numberOfRowsSelected,
            before: pagination.cursor,
          };
    return (
      <Query
        query={CREDIT_TRANSACTION_LIST}
        variables={{
          ...filters,
          ...paginationVariables,
        }}
        notifyOnNetworkStatusChange
        fetchPolicy="cache-and-network"
      >
        {({ data, loading, fetchMore }) => {
          this.fetchMore = fetchMore;
          return render({ data, loading });
        }}
      </Query>
    );
  };

  /**
   * @description
   * Fetch more rows when user change page.
   *
   * @param {Object} paginatorVariables - Pagination query's variables.
   */
  handleChangePage = ({ direction, cursor }) => {
    const history = this.context;

    this.setState({ pagination: { direction, cursor } });
    const { pathname } = history.location;
    const queryString = getQueryString(history);
    const urlString = qs.stringify({
      ...queryString,
      direction,
      cursor,
    });
    history.push({ pathname, search: urlString });
  };

  /**
   * @description
   * Change number of rows selected.
   *
   * @param {number} numberOfRowsSelected - New number of rows selected.
   */
  handleChangeNumberOfRows = numberOfRowsSelected => {
    const history = this.context;

    this.setState({
      numberOfRowsSelected,
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const queryString = getQueryString(history);
    const urlString = qs.stringify({
      ...queryString,
      numberOfRowsSelected,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: urlString });
  };

  /**
   * @description
   * Filter rows according selected filters.
   *
   * @param {Object} filters - Filters to apply.
   */
  handleFilter = filters => {
    const history = this.context;
    this.setState({
      filters: { ...defaultFilters, ...filters },
      pagination: { direction: null, cursor: undefined },
    });
    const { pathname } = history.location;
    const { numberOfRowsSelected } = getQueryString(history);
    const urlString = qs.stringify({
      ...filters,
      numberOfRowsSelected,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname, search: urlString });
  };

  /**
   * @description
   * Change state to restart filters.
   */
  handleRestartFilters = () => {
    const history = this.context;
    this.setState({
      filters: { ...defaultFilters },
      pagination: { direction: null, cursor: undefined },
    });
    const { pathname } = history.location;
    const { numberOfRowsSelected } = getQueryString(history);
    const urlString = qs.stringify({
      numberOfRowsSelected,
    });

    history.push({ pathname, search: urlString });
  };

  render() {
    const { numberOfRowsSelected, pagination, filters } = this.state;

    return (
      <this.Compose>
        {({ listCreditTransactions: { loading, data } }) => (
          <CreditList
            data={data}
            loading={Object.keys(data).length === 0}
            fullLoading={loading}
            numberOfRowsSelected={numberOfRowsSelected}
            onChangePage={paginatorVariables => {
              this.handleChangePage(paginatorVariables);
            }}
            onChangeNumberOfRows={this.handleChangeNumberOfRows}
            paginationDirection={pagination.direction}
            appliedFilters={filters}
            onFilter={this.handleFilter}
            onRestartFilters={this.handleRestartFilters}
          />
        )}
      </this.Compose>
    );
  }
}

/**
 * CreditTransactionListContainer PropTypes
 * @param t - prop from HOC with string translations
 * @param initialFilters - initial Filters to apply
 */

CreditsListContainer.propTypes = {
  initialFilters: PropTypes.shape({}).isRequired,
};
export default withTranslation()(CreditsListContainer);
